/**
 * The Black Market — the shop window's third face (Janeks, 2026-10-20).
 *
 * Players resell what they bought for Gold, beside listings from the bot
 * population; every card shows the item, its price, its seller and a Buy
 * button, and hovering it (tapping, on touch) shows the full item card. "My
 * listings" and "My sales" are the seller's side. The rules — the 5% fee, the
 * cap at what was paid, the bot buyers — live on the server
 * (`backend/src/game/market/`); this only draws them.
 *
 * Embedded like the Atelier: one root, built once and MOVED into the shop body
 * when this face is showing, with its own delegated listeners and its own two
 * tooltips (hover, and a pinned one for touch). Its cards carry `data-bm-*`
 * attributes, never the shop's `data-tip` / `data-buy`, so the shop's own body
 * handlers ignore them.
 */
import { gameStateStore } from '../../state/game-state';
import {
  buyListing, cancelListing, closeSell, feeFor, loadMarket, marketStore, openSell, pickSellUnit, resetMarketState,
  setMarketFilter, setMarketView, setSellPrice, submitListing,
} from '../../state/market-state';
import type {
  MarketCategory, MarketListing, MarketSale, MarketSellable, MarketState, MarketView,
} from '../../state/market-state.types';
import { ItemTooltip, itemTooltipHtml } from './item-tooltip';
import { pictureHtml } from './paperdoll';
import { goldAmount, windowHeading } from './window-chrome';

const VIEWS: readonly { id: MarketView; label: string }[] = [
  { id: 'browse', label: 'Browse' },
  { id: 'mine', label: 'My listings' },
  { id: 'sales', label: 'My sales' },
];

const CATEGORIES: readonly { id: MarketCategory; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'WEAPONS', label: 'Weapons' },
  { id: 'APPAREL', label: 'Apparel' },
  { id: 'PACKS', label: 'Bags' },
  { id: 'CONSUMABLES', label: 'Consumables' },
];

let root: HTMLElement | null = null;
let tooltip: ItemTooltip | null = null;
let pinned: ItemTooltip | null = null;
let unsubs: (() => void)[] = [];
let active = false;
let lastSig = '';
let lastPointerType = 'mouse';

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function gold(): number {
  const c = gameStateStore.get().character;
  return c?.state === 'ACTIVE' ? c.premiumBalance : 0;
}

// ─── lifecycle ───────────────────────────────────────────────────────────────

export function blackMarketRoot(): HTMLElement {
  if (root) return root;
  root = document.createElement('div');
  root.className = 'bv-bm';
  root.addEventListener('click', onClick);
  root.addEventListener('input', onInput);
  root.addEventListener('pointerdown', (e) => { lastPointerType = e.pointerType || 'mouse'; });
  root.addEventListener('pointerover', onPointerOver);
  root.addEventListener('pointermove', onPointerMove);
  root.addEventListener('pointerleave', () => tooltip?.hide());
  root.addEventListener('keydown', onKeyDown);
  return root;
}

/** Called each repaint while this face shows; loads and subscribes once per visit. */
export function activateBlackMarket(): void {
  blackMarketRoot();
  if (active) return;
  active = true;
  tooltip ??= new ItemTooltip();
  if (!pinned) {
    pinned = new ItemTooltip();
    pinned.el.classList.add('bv-tip-pinned');
    pinned.el.setAttribute('role', 'dialog');
    pinned.el.addEventListener('click', onPinnedClick);
  }
  document.addEventListener('pointerdown', onDocPointerDown, true);
  unsubs = [marketStore.subscribe(() => paint()), gameStateStore.subscribe(() => paint())];
  void loadMarket();
}

export function deactivateBlackMarket(): void {
  if (!active) return;
  active = false;
  for (const u of unsubs) u();
  unsubs = [];
  document.removeEventListener('pointerdown', onDocPointerDown, true);
  tooltip?.hide();
  pinned?.hide();
  lastSig = '';
}

/** The shop window closed: forget the visit. */
export function closeBlackMarket(): void {
  deactivateBlackMarket();
  resetMarketState();
}

export function disposeBlackMarket(): void {
  closeBlackMarket();
  tooltip?.dispose();
  pinned?.dispose();
  tooltip = pinned = null;
  root?.remove();
  root = null;
}

/** Inner Escape: closes the sell prompt or the pinned card before the window. Returns whether it handled it. */
export function blackMarketEscape(): boolean {
  if (pinned?.isVisible) { pinned.hide(); return true; }
  if (marketStore.get().sell) { closeSell(); return true; }
  return false;
}

// ─── rendering ───────────────────────────────────────────────────────────────

function paint(): void {
  if (!root || !active) return;
  const s = marketStore.get();
  const g = gold();
  // The price slider must not be rebuilt under the player's thumb: a change
  // that only moves the price patches the preview in place.
  const sig = JSON.stringify({ ...s, sell: s.sell ? { ...s.sell, priceGold: 0 } : null, g });
  if (sig === lastSig) { patchSellPreview(s); return; }
  lastSig = sig;
  root.innerHTML = blackMarketHtml(s, g, Date.now());
}

/** The whole face. PURE, so tests assert on markup without mounting anything. */
export function blackMarketHtml(s: MarketState, currentGold: number, now: number): string {
  const views = VIEWS.map((v) => `<button type="button" role="tab" class="bv-shop-tab bv-bm-view${s.view === v.id ? ' bv-shop-tab-on' : ''}"
    aria-selected="${s.view === v.id}" data-bm-view="${v.id}">${v.label}</button>`).join('');
  return `
    <div class="bv-bm-head">
      ${windowHeading('Black Market', 'Gold goods, second hand. Only what was bought for Gold is sold here.')}
      <div class="bv-bm-headbar">
        <span class="bv-shop-wallet bv-bm-wallet">${goldAmount(currentGold)}</span>
        <button type="button" class="bv-act bv-act-go bv-bm-sell-open" data-bm-sell-open>Sell an item</button>
      </div>
    </div>
    <div class="bv-shop-tabs bv-bm-views" role="tablist" aria-label="Black Market">${views}</div>
    ${s.error ? `<p class="bv-win-err" role="alert">${esc(s.error)}</p>` : ''}
    ${s.notice ? `<p class="bv-win-ok" role="status">${esc(s.notice)}</p>` : ''}
    ${s.view === 'browse' ? browseHtml(s, currentGold, now) : s.view === 'mine' ? mineHtml(s, now) : salesHtml(s, now)}
    ${s.sell ? sellHtml(s) : ''}`;
}

function browseHtml(s: MarketState, currentGold: number, now: number): string {
  const chips = CATEGORIES.map((c) => `<button type="button" class="bv-bm-chip${s.category === c.id ? ' bv-bm-chip-on' : ''}"
    aria-pressed="${s.category === c.id}" data-bm-cat="${c.id}">${c.label}</button>`).join('');
  const sorts = (['newest', 'cheapest'] as const).map((k) => `<button type="button" class="bv-bm-chip${s.sort === k ? ' bv-bm-chip-on' : ''}"
    aria-pressed="${s.sort === k}" data-bm-sort="${k}">${k === 'newest' ? 'Newest' : 'Cheapest'}</button>`).join('');
  const page = s.listings;
  let body: string;
  if (!page) body = note(s.busy ? 'Loading the stalls…' : 'Nothing to show.');
  else if (page.items.length === 0) body = note('Nothing listed in this aisle right now. Check back soon.');
  else body = `<div class="bv-shop-grid bv-bm-grid">${page.items.map((l) => listingCardHtml(l, s.busy, currentGold, now)).join('')}</div>`;
  const pages = page ? Math.max(1, Math.ceil(page.total / page.pageSize)) : 1;
  const pager = page && pages > 1 ? `<div class="bv-bm-pager">
      <button type="button" class="bv-act" data-bm-page="${page.page - 1}" ${page.page <= 1 ? 'disabled' : ''}>Previous</button>
      <span>Page ${page.page} of ${pages}</span>
      <button type="button" class="bv-act" data-bm-page="${page.page + 1}" ${page.page >= pages ? 'disabled' : ''}>Next</button>
    </div>` : '';
  return `<div class="bv-bm-filters"><div class="bv-bm-chips" role="group" aria-label="Category">${chips}</div>
    <div class="bv-bm-chips" role="group" aria-label="Sort">${sorts}</div></div>${body}${pager}`;
}

function mineHtml(s: MarketState, now: number): string {
  if (!s.mine) return note(s.busy ? 'Loading…' : 'Nothing to show.');
  if (s.mine.length === 0) return note('You have nothing listed. Items you bought for Gold can be sold here.');
  return `<div class="bv-shop-grid bv-bm-grid">${s.mine.map((l) => `<div class="well bv-shop-card bv-bm-card" data-bm-tip="${esc(l.id)}">
      ${cardTop(l)}
      <div class="bv-shop-card-name">${esc(l.name)}</div>
      <div class="bv-bm-meta">Listed ${esc(ago(l.listedAt, now))} · you get ${l.priceGold - feeFor(l.priceGold)}</div>
      <div class="bv-shop-card-foot">
        <span class="bv-shop-card-price">${goldAmount(l.priceGold)}</span>
        <button type="button" class="bv-act bv-bm-cancel" data-bm-cancel="${esc(l.id)}" ${s.busy ? 'disabled' : ''}>Take down</button>
      </div>
    </div>`).join('')}</div>`;
}

function salesHtml(s: MarketState, now: number): string {
  if (!s.sales) return note(s.busy ? 'Loading…' : 'Nothing to show.');
  if (s.sales.length === 0) return note('No sales yet.');
  const rows = s.sales.map((x) => `<li class="well bv-bm-sale" data-bm-tip="${esc(x.id)}">
      <span class="bv-bm-sale-art">${pictureHtml(x.iconPath, x.name, 'bv-bm-sale-img', '48px', { kind: x.kind, slot: x.stats.slot })}</span>
      <span class="bv-bm-sale-main">
        <span class="bv-bm-sale-name">${esc(x.name)}</span>
        <span class="bv-bm-meta">Sold ${esc(ago(x.soldAt, now))}${x.buyerName ? ` to ${esc(x.buyerName)}` : ''}</span>
      </span>
      <span class="bv-bm-sale-money">
        <span>${goldAmount(x.priceGold)}</span>
        <span class="bv-bm-fee">fee −${x.feeGold}</span>
        <span class="bv-bm-net">you got ${goldAmount(x.netGold)}</span>
      </span>
    </li>`).join('');
  return `<ul class="bv-bm-sales">${rows}</ul>`;
}

function cardTop(
  l: { iconPath: string | null; name: string; kind: string; stats: { slot: string | null }; quantity: number },
  yours = false,
): string {
  const art = pictureHtml(l.iconPath, l.name, 'bv-shop-card-art', '96px', { kind: l.kind, slot: l.stats.slot });
  const qty = l.quantity > 1 ? `<span class="bv-shop-card-qty">×${l.quantity}</span>` : '';
  // A taped label, not a pseudo-element: the card's ::before/::after are its
  // corner brackets, and a label drawn there was squeezed into a 9px box.
  const tag = yours ? '<span class="bv-bm-yours">Yours</span>' : '';
  return `<div class="bv-shop-card-top">${tag}${art || '<span class="bv-shop-card-noart" aria-hidden="true">?</span>'}${qty}</div>`;
}

/** Why Buy is dead, or '' when it isn't. */
export function buyBlockedReason(l: MarketListing, busy: boolean, currentGold: number): string {
  if (l.mine) return 'This is your own listing';
  if (l.owned) return 'You already own this one';
  if (currentGold < l.priceGold) return `${l.priceGold - currentGold} Gold short`;
  if (busy) return 'Working…';
  return '';
}

export function listingCardHtml(l: MarketListing, busy: boolean, currentGold: number, now: number): string {
  const reason = buyBlockedReason(l, busy, currentGold);
  return `<div class="well bv-shop-card bv-bm-card${l.mine ? ' bv-bm-card-mine' : ''}" data-bm-tip="${esc(l.id)}">
    ${cardTop(l, l.mine)}
    <div class="bv-shop-card-name">${esc(l.name)}</div>
    <div class="bv-bm-meta">${l.mine ? 'Listed' : `by ${esc(l.sellerName)} ·`} ${esc(ago(l.listedAt, now))}</div>
    <div class="bv-shop-card-foot">
      <span class="bv-shop-card-price">${goldAmount(l.priceGold)}</span>
      <button type="button" class="bv-act bv-act-go bv-shop-buy" data-bm-buy="${esc(l.id)}"
        ${reason ? `disabled title="${esc(reason)}"` : ''}>Buy</button>
    </div>
  </div>`;
}

function sellHtml(s: MarketState): string {
  const units = s.sellable;
  const draft = s.sell!;
  let inner: string;
  if (!units) {
    inner = note('Checking your hideout…');
  } else if (units.length === 0) {
    inner = note('Nothing to sell. Only items bought for Gold can be sold here, and they have to be in your hideout — not worn, not packed.');
  } else {
    const picks = units.map((u) => `<button type="button" class="bv-bm-unit${draft.userItemId === u.userItemId ? ' bv-bm-unit-on' : ''}"
        aria-pressed="${draft.userItemId === u.userItemId}" data-bm-unit="${esc(u.userItemId)}">
        ${pictureHtml(u.iconPath, u.name, 'bv-bm-unit-img', '56px', { kind: u.kind, slot: u.stats.slot })}
        <span class="bv-bm-unit-name">${esc(u.name)}</span>
      </button>`).join('');
    const unit = units.find((u) => u.userItemId === draft.userItemId);
    inner = `<div class="bv-bm-units" role="group" aria-label="Item to sell">${picks}</div>${unit ? priceHtml(unit, draft.priceGold, s.busy) : ''}`;
  }
  // The shop's own confirm plate (commerce.css.ts .bv-shop-prompt-panel), so the
  // two dialogs in one window are the same object.
  return `<div class="bv-scrim bv-shop-prompt bv-bm-sell-scrim" data-bm-sell-scrim>
    <div class="panel grained bv-win bv-shop-prompt-panel bv-bm-sell" role="dialog" aria-modal="true" aria-labelledby="bm-sell-title">
      <h3 class="bv-win-title" id="bm-sell-title">Sell an item</h3>
      ${inner}
      ${s.error ? `<p class="bv-win-err" role="alert">${esc(s.error)}</p>` : ''}
      <div class="bv-bm-sell-actions">
        <button type="button" class="bv-act" data-bm-sell-close>Cancel</button>
        ${units && units.length > 0 ? `<button type="button" class="bv-act bv-act-go" data-bm-sell-submit
          ${!draft.userItemId || s.busy || !validPrice(draft.priceGold, units.find((u) => u.userItemId === draft.userItemId)) ? 'disabled' : ''}>List it</button>` : ''}
      </div>
    </div>
  </div>`;
}

function validPrice(price: number, unit: MarketSellable | undefined): boolean {
  return !!unit && Number.isInteger(price) && price >= 1 && price <= unit.priceCap;
}

function priceHtml(unit: MarketSellable, price: number, busy: boolean): string {
  return `<div class="bv-bm-price">
    <label class="bv-bm-price-label" for="bm-price">Price</label>
    <div class="bv-bm-price-row">
      <input type="range" id="bm-price-range" min="1" max="${unit.priceCap}" value="${price}" data-bm-price aria-label="Price slider" ${busy ? 'disabled' : ''}>
      <input type="number" id="bm-price" inputmode="numeric" min="1" max="${unit.priceCap}" value="${price}" data-bm-price ${busy ? 'disabled' : ''}>
    </div>
    <p class="bv-bm-preview" data-bm-preview>${previewText(price, unit.priceCap)}</p>
  </div>`;
}

/** "Fee 5%: −50 · You get 950" — or why the price can't be used. */
export function previewText(price: number, cap: number): string {
  if (!Number.isInteger(price) || price < 1) return 'Enter a price of at least 1 Gold.';
  if (price > cap) return `That is more than the ${cap} Gold cap.`;
  return `Fee 5%: −${feeFor(price)} · You get ${price - feeFor(price)} Gold`;
}

function patchSellPreview(s: MarketState): void {
  if (!root || !s.sell) return;
  const unit = s.sellable?.find((u) => u.userItemId === s.sell!.userItemId);
  if (!unit) return;
  const price = s.sell.priceGold;
  const preview = root.querySelector<HTMLElement>('[data-bm-preview]');
  if (preview) preview.textContent = previewText(price, unit.priceCap);
  for (const input of root.querySelectorAll<HTMLInputElement>('input[data-bm-price]')) {
    if (document.activeElement !== input && input.value !== String(price)) input.value = String(price);
  }
  const submit = root.querySelector<HTMLButtonElement>('[data-bm-sell-submit]');
  if (submit) submit.disabled = s.busy || !validPrice(price, unit);
}

function note(text: string): string {
  return `<p class="bv-shop-note bv-bm-note">${esc(text)}</p>`;
}

/** "just now", "12m ago", "3h ago", "2d ago". */
export function ago(iso: string, now: number): string {
  const ms = Math.max(0, now - Date.parse(iso));
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ─── item cards ──────────────────────────────────────────────────────────────

function findItem(id: string): { item: MarketListing | MarketSale; listing: MarketListing | null } | null {
  const s = marketStore.get();
  const l = s.listings?.items.find((x) => x.id === id) ?? s.mine?.find((x) => x.id === id);
  if (l) return { item: l, listing: l };
  const sale = s.sales?.find((x) => x.id === id);
  return sale ? { item: sale, listing: null } : null;
}

function cardHtmlFor(id: string, withActions: boolean): string | null {
  const found = findItem(id);
  if (!found) return null;
  const { item, listing } = found;
  const wear = item.durability != null ? ` · durability ${item.durability}/${item.stats.maxDurability}` : '';
  let actionsHtml: string | undefined;
  if (withActions && listing && marketStore.get().view === 'browse') {
    const reason = buyBlockedReason(listing, marketStore.get().busy, gold());
    actionsHtml = `<div class="bv-tip-actions"><button type="button" class="bv-slot-btn bv-slot-btn-buy" data-bm-buy="${esc(listing.id)}"
      ${reason ? 'disabled' : ''}>${reason ? esc(reason) : `Buy for ${listing.priceGold} Gold`}</button></div>`;
  }
  return itemTooltipHtml({
    name: item.name,
    kind: item.kind,
    stats: item.stats,
    priceLine: `${item.priceGold} Gold${wear}`,
    ...(actionsHtml ? { actionsHtml } : {}),
  });
}

function onPointerOver(e: PointerEvent): void {
  if (e.pointerType === 'touch' || pinned?.isVisible) return;
  const card = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-bm-tip]');
  const html = card?.dataset.bmTip ? cardHtmlFor(card.dataset.bmTip, false) : null;
  if (!html) { tooltip?.hide(); return; }
  tooltip?.show(html, e.clientX, e.clientY);
}

function onPointerMove(e: PointerEvent): void {
  if (!tooltip?.isVisible) return;
  if (!(e.target as HTMLElement | null)?.closest('[data-bm-tip]')) { tooltip.hide(); return; }
  tooltip.move(e.clientX, e.clientY);
}

/** Touch has no hover: a tap on a card pins its item card beside it, with Buy on it. */
function pin(card: HTMLElement): void {
  const html = card.dataset.bmTip ? cardHtmlFor(card.dataset.bmTip, true) : null;
  if (!html || !pinned) return;
  tooltip?.hide();
  const r = card.getBoundingClientRect();
  pinned.show(html, r.right, r.top);
}

function onPinnedClick(e: MouseEvent): void {
  const buy = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-bm-buy]');
  if (!buy || buy.disabled || !buy.dataset.bmBuy) return;
  pinned?.hide();
  void buyListing(buy.dataset.bmBuy);
}

function onDocPointerDown(e: PointerEvent): void {
  if (!pinned?.isVisible || pinned.el.contains(e.target as Node)) return;
  pinned.hide();
}

// ─── input ───────────────────────────────────────────────────────────────────

function onClick(e: MouseEvent): void {
  const t = e.target as HTMLElement;
  const hit = <T extends HTMLElement = HTMLElement>(sel: string) => t.closest<T>(sel);

  const view = hit('[data-bm-view]');
  if (view) { setMarketView(view.dataset.bmView as MarketView); return; }
  const cat = hit('[data-bm-cat]');
  if (cat) { setMarketFilter({ category: cat.dataset.bmCat as MarketCategory }); return; }
  const sort = hit('[data-bm-sort]');
  if (sort) { setMarketFilter({ sort: sort.dataset.bmSort as 'newest' | 'cheapest', category: marketStore.get().category }); return; }
  const page = hit<HTMLButtonElement>('[data-bm-page]');
  if (page && !page.disabled) {
    const s = marketStore.get();
    setMarketFilter({ category: s.category, sort: s.sort, page: Number(page.dataset.bmPage) });
    return;
  }
  const buy = hit<HTMLButtonElement>('[data-bm-buy]');
  if (buy) { if (!buy.disabled && buy.dataset.bmBuy) void buyListing(buy.dataset.bmBuy); return; }
  const cancel = hit<HTMLButtonElement>('[data-bm-cancel]');
  if (cancel) { if (!cancel.disabled && cancel.dataset.bmCancel) void cancelListing(cancel.dataset.bmCancel); return; }
  if (hit('[data-bm-sell-open]')) { void openSell(); return; }
  if (hit('[data-bm-sell-close]')) { closeSell(); return; }
  if (t.matches('[data-bm-sell-scrim]')) { closeSell(); return; }
  const unit = hit('[data-bm-unit]');
  if (unit?.dataset.bmUnit) { pickSellUnit(unit.dataset.bmUnit); return; }
  const submit = hit<HTMLButtonElement>('[data-bm-sell-submit]');
  if (submit) { if (!submit.disabled) void submitListing(); return; }

  // A tap on a card (not its button) pins the item card — touch has no hover.
  const card = hit('[data-bm-tip]');
  if (card && lastPointerType !== 'mouse') pin(card);
}

function onInput(e: Event): void {
  const input = (e.target as HTMLElement).closest<HTMLInputElement>('input[data-bm-price]');
  if (!input) return;
  const n = Math.floor(Number(input.value));
  setSellPrice(Number.isFinite(n) ? n : 0);
}

function onKeyDown(e: KeyboardEvent): void {
  if (e.key === 'Enter' && (e.target as HTMLElement).matches('input[data-bm-price]')) {
    const submit = root?.querySelector<HTMLButtonElement>('[data-bm-sell-submit]');
    if (submit && !submit.disabled) { e.preventDefault(); void submitListing(); }
  }
}
